import * as React from 'react';

import { VehicleOwnership } from '../../../__generated__/globalTypes';
import { useUpsertVehicle } from '../../../hooks/mutations/useUpsertVehicle';

import { buildVehicleCreateInput, buildVehicleTitle, hasVehicleDetails } from './possessionsCardModel';
import { VehicleCardBase } from './VehicleCard';

export interface IDraftVehicleCardProps {
  /** Called after the new vehicle is persisted (and `GetVehicles` refetched) — drop the draft. */
  onCreated: () => void;
  /** Called when the user discards the draft via the header trash — drop the draft. */
  onDiscard: () => void;
}

/**
 * An unsaved vehicle card added by the "Add vehicle" affordance. Ownership is
 * pre-selected to "Own outright" (it's a required field), and the row persists —
 * creating a family-shared vehicle — only once a make or model is entered (on
 * blur), so an empty draft never hits the server. The header trash discards the
 * draft; after a successful create the refetched `GetVehicles` row replaces it.
 */
const DraftVehicleCard = ({ onCreated, onDiscard }: IDraftVehicleCardProps) => {
  const [upsertVehicle, { loading: isSaving }] = useUpsertVehicle();
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [make, setMake] = React.useState('');
  const [model, setModel] = React.useState('');
  const [ownership, setOwnership] = React.useState<VehicleOwnership>(VehicleOwnership.OwnOutright);
  // Synchronous latch so a near-simultaneous blur + ownership-change can't both
  // dispatch a create (Apollo's `loading` only flips a microtask later).
  const creatingRef = React.useRef(false);

  const persist = React.useCallback(
    (nextMake: string, nextModel: string, nextOwnership: VehicleOwnership) => {
      if (creatingRef.current || !hasVehicleDetails(nextMake, nextModel)) {
        return;
      }
      creatingRef.current = true;
      void upsertVehicle({
        variables: { input: buildVehicleCreateInput(nextMake, nextModel, nextOwnership) },
        refetchQueries: ['GetVehicles'],
        awaitRefetchQueries: true,
      })
        .then(onCreated)
        .catch(() => {
          creatingRef.current = false;
        });
    },
    [upsertVehicle, onCreated]
  );

  const handleOwnershipChange = (value: VehicleOwnership) => {
    setOwnership(value);
    persist(make, model, value);
  };

  return (
    <VehicleCardBase
      title={buildVehicleTitle(null, make, model)}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded((v) => !v)}
      make={make}
      model={model}
      ownership={ownership}
      onMakeChange={setMake}
      onModelChange={setModel}
      onOwnershipChange={handleOwnershipChange}
      onCommit={() => persist(make, model, ownership)}
      onDelete={onDiscard}
      // Locked while the create is in flight — this draft is replaced by the
      // refetched row on success, so typing into it would be silently discarded.
      isDisabled={isSaving}
    />
  );
};

export default DraftVehicleCard;
